/**
 * Core Routes Configuration  -  Single Source of Truth
 * 
 * ALL route paths and display labels for the application live here.
 * Sidebar, Header, AnimatedRoutes, and ProtectedRoute all consume this config.
 * 
 * ⚠️  DO NOT remove entries without updating all consumers.
 *     The navigation integrity guard will flag missing routes in dev mode.
 */

export interface RouteEntry {
  path: string;
  label: string;
  /** Header breadcrumb title (defaults to label) */
  headerTitle?: string;
}

// ── Core protected routes that must ALWAYS be accessible ──
export const CORE_ROUTES = {
  dashboard:       { path: "/dashboard",          label: "Dashboard" },
  visionAssistant: { path: "/vision-assistant",   label: "Vision Assistant" },
  services:        { path: "/services",            label: "Services" },
  support:         { path: "/support",             label: "Support" },
  plan:            { path: "/plan",                label: "My Plan" },
  brandGuide:      { path: "/brand-guide",         label: "Brand Guide" },
  topics:          { path: "/topics",              label: "Topic Ideas" },
  preLive:         { path: "/generator/pre-live",  label: "Pre-Live Content" },
  duringLive:      { path: "/generator/during-live", label: "During Live" },
  postLive:        { path: "/generator/post-live", label: "Post-Live Content" },
  settings:        { path: "/settings",            label: "Settings" },
  admin:           { path: "/admin",               label: "Admin Panel" },
  history:         { path: "/history",             label: "Content History" },
  // Label only — the PATH stays /marketing-audit. Renaming it would need an entry in
  // ROUTE_ALIASES and would break existing links for no benefit; the breadcrumb text
  // is what Kate and Bridget flagged (VA-139), and the sidebar already said
  // "Analyze Brand", so the two had drifted apart.
  marketingAudit:  { path: "/marketing-audit",     label: "Analyze Brand" },
  // The prototype's Agency area (app.html, `go('agency')`). Its tabs there are
  // AI providers / Connections / Brand Reports. Only Brand Reports is built —
  // it has real backing tables already (brand_reports, brand_report_schedules).
  // The other two are deliberately not stubbed; see docs and VA-165.
  agency:          { path: "/agency",               label: "Agency" },
  coaching:        { path: "/coaching",             label: "Live Coaching" },
  contentAssistant: { path: "/content-assistant",  label: "Content Assistant" },
} as const satisfies Record<string, RouteEntry>;

// ── Public routes (no auth required) ──
export const PUBLIC_ROUTES = {
  auth:              { path: "/auth",               label: "Sign In" },
  resetPassword:     { path: "/reset-password",     label: "Reset Password" },
  join:              { path: "/join",                label: "Join" },
  community:         { path: "/community",           label: "Community" },
  membershipSuccess: { path: "/membership-success",  label: "Membership Success" },
  accountPaused:     { path: "/account-paused",      label: "Account Paused" },
} as const satisfies Record<string, RouteEntry>;

// ── Route aliases (redirects) ──
export const ROUTE_ALIASES: Record<string, string> = {
  "/credits":          CORE_ROUTES.plan.path,
  "/credits/*":        CORE_ROUTES.plan.path,
  "/generator":        CORE_ROUTES.preLive.path,
  "/pre-live":         CORE_ROUTES.preLive.path,
  "/post-live":        CORE_ROUTES.postLive.path,
  "/during-live":      CORE_ROUTES.duringLive.path,
  "/brands":           CORE_ROUTES.dashboard.path,
  "/brand-voice":      CORE_ROUTES.brandGuide.path,
  "/customer-avatar":  CORE_ROUTES.brandGuide.path,
  "/join-free":        PUBLIC_ROUTES.join.path,
  "/vision-chat":      CORE_ROUTES.visionAssistant.path,
};

// ── Header title map (built from CORE_ROUTES) ──
export const HEADER_TITLES: Record<string, string> = Object.values(CORE_ROUTES).reduce(
  (acc, route) => {
    acc[route.path] = route.label;
    return acc;
  },
  {} as Record<string, string>
);

/**
 * Routes that MUST appear in sidebar navigation.
 * Used by the integrity guard to detect accidental removals.
 */
export const REQUIRED_SIDEBAR_PATHS = [
  CORE_ROUTES.dashboard.path,
  CORE_ROUTES.visionAssistant.path,
  CORE_ROUTES.services.path,
  CORE_ROUTES.support.path,
  CORE_ROUTES.plan.path,
  CORE_ROUTES.settings.path,
  CORE_ROUTES.history.path,
] as const;

/**
 * Friendly route names for auth redirect messages.
 */
export const ROUTE_DISPLAY_NAMES: Record<string, string> = Object.values(CORE_ROUTES).reduce(
  (acc, route) => {
    acc[route.path] = route.label;
    return acc;
  },
  {} as Record<string, string>
);
